import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SectionLabel } from "@/components/Reveal";
import ChartVisual from "@/components/ChartVisual";

const WhyAtlas = () => {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const chartOp = useTransform(p, [0, 0.2], [0.45, 0]);
  const mtOp = useTransform(p, [0.16, 0.3, 0.6, 0.72], [0, 1, 1, 0]);
  const mtY = useTransform(p, [0.16, 0.38], [90, 0]);
  const lionOp = useTransform(p, [0.42, 0.55, 0.76, 0.88], [0, 1, 1, 0]);
  const lionScale = useTransform(p, [0.42, 0.88], [1.18, 1]);
  const logoOp = useTransform(p, [0.74, 0.88], [0, 1]);
  const logoScale = useTransform(p, [0.74, 0.95], [0.82, 1]);

  const b1 = useTransform(p, [0.04, 0.12, 0.2, 0.27], [0, 1, 1, 0]);
  const b2 = useTransform(p, [0.28, 0.36, 0.46, 0.53], [0, 1, 1, 0]);
  const b3 = useTransform(p, [0.52, 0.6, 0.68, 0.75], [0, 1, 1, 0]);
  const b4 = useTransform(p, [0.84, 0.94], [0, 1]);

  return (
    <section id="why-atlas" ref={ref} className="relative bg-black" style={{ height: "460vh" }} data-testid="why-atlas-section">
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute top-24 sm:top-28 left-0 right-0 text-center px-5">
          <SectionLabel>Why Atlas?</SectionLabel>
        </div>

        <div className="relative w-full max-w-3xl h-[42vh] sm:h-[48vh] px-5" data-testid="why-atlas-stage">
          <motion.div style={{ opacity: chartOp }} className="absolute inset-0 flex items-center">
            <ChartVisual seed={7} layers={{ zones: true, ribbon: true }} className="w-full h-auto" />
          </motion.div>

          <motion.div style={{ opacity: mtOp, y: mtY }} className="absolute inset-x-0 bottom-0" data-testid="why-atlas-mountains">
            <svg viewBox="0 0 800 260" className="w-full h-auto" role="img" aria-label="Atlas Mountains silhouette">
              <path d="M0,260 L0,200 L90,140 L160,190 L250,88 L330,180 L420,120 L510,200 L600,108 L690,190 L800,150 L800,260 Z"
                fill="#0E131C" stroke="#D4AF37" strokeOpacity="0.4" strokeWidth="1.2" />
              <path d="M0,260 L0,232 L120,192 L230,232 L360,172 L480,226 L620,182 L720,222 L800,202 L800,260 Z"
                fill="#0B0E14" stroke="#1E293B" strokeWidth="1" />
            </svg>
          </motion.div>

          <motion.div style={{ opacity: lionOp, scale: lionScale }} className="absolute inset-0 flex items-center justify-center" data-testid="why-atlas-lion">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gold/15 blur-2xl" />
              <img
                src="/atlas-lion.jpg"
                alt=""
                loading="lazy"
                className="relative w-44 h-44 sm:w-60 sm:h-60 rounded-full object-cover ring-1 ring-gold/40"
                style={{ filter: "brightness(0.92) contrast(1.05)" }}
              />
              <div className="absolute inset-0 rounded-full bg-gold/10 mix-blend-overlay" />
            </div>
          </motion.div>

          <motion.div style={{ opacity: logoOp, scale: logoScale }} className="absolute inset-0 flex items-center justify-center" data-testid="why-atlas-logo">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gold/25 blur-3xl" />
              <img src="/atlas-logo-256.png" alt="Atlas Trading Community" className="relative w-40 h-40 sm:w-52 sm:h-52 rounded-full ring-1 ring-gold/50 object-cover" />
            </div>
          </motion.div>
        </div>

        <div className="relative h-28 sm:h-32 w-full max-w-2xl px-6 text-center">
          <motion.p style={{ opacity: b1 }} className="absolute inset-x-6 top-0 font-display font-bold text-xl sm:text-2xl text-slate-100 leading-snug" data-testid="why-atlas-beat-1">
            The name Atlas carries more than trading.
          </motion.p>
          <motion.p style={{ opacity: b2 }} className="absolute inset-x-6 top-0 font-display font-bold text-xl sm:text-2xl text-slate-100 leading-snug" data-testid="why-atlas-beat-2">
            From the Atlas Mountains of Morocco —<br className="hidden sm:block" /> where the story begins.
          </motion.p>
          <motion.p style={{ opacity: b3 }} className="absolute inset-x-6 top-0 font-display font-bold text-xl sm:text-2xl text-slate-100 leading-snug" data-testid="why-atlas-beat-3">
            The Atlas Lion. Strength and presence —<br className="hidden sm:block" /> a legacy that never disappeared.
          </motion.p>
          <motion.div style={{ opacity: b4 }} className="absolute inset-x-6 top-0" data-testid="why-atlas-beat-4">
            <p className="font-display font-extrabold text-2xl sm:text-3xl text-slate-50 tracking-tight">
              Strength. Discipline. <span className="text-gold-gradient">Independence.</span>
            </p>
            <p className="mt-3 font-mono text-[10px] sm:text-[11px] tracking-[0.35em] text-gold/80 uppercase">
              Analysis · Technology · Independence
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyAtlas;
